<template>
  <div id="app" :data-theme="theme">
    <el-container v-if="$route.path !== '/login' && $route.path !== '/register'" class="layout-container">
      <!-- 导航栏：玻璃拟态 + 顶部固定 -->
      <el-header class="layout-header nav-glass" height="56px">
        <div class="header-left" @click="goDashboard">
          <div class="header-logo">
            <!-- 电梯图标 -->
            <svg class="logo-icon" viewBox="0 0 32 32" width="28" height="28">
              <defs>
                <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#1890ff"/>
                  <stop offset="100%" stop-color="#722ed1"/>
                </linearGradient>
              </defs>
              <rect x="4" y="2" width="24" height="28" rx="4" fill="none" stroke="url(#logoGrad)" stroke-width="2.5"/>
              <line x1="16" y1="6" x2="16" y2="26" stroke="url(#logoGrad)" stroke-width="2"/>
              <polygon points="9,18 16,10 23,18" fill="url(#logoGrad)"/>
            </svg>
            <span class="logo-text">新维AI智慧电梯管理平台</span>
          </div>
        </div>

        <!-- 一级菜单 -->
        <el-menu mode="horizontal" :default-active="activeMenu" router class="header-menu" background-color="transparent" text-color="var(--text-secondary)" active-text-color="var(--color-primary)">
          <el-menu-item index="/dashboard">
            <i class="el-icon-data-analysis"></i>
            <span>电梯管理看板</span>
          </el-menu-item>
          <el-menu-item index="/project">
            <i class="el-icon-s-grid"></i>
            <span>基础信息管理</span>
          </el-menu-item>
          <el-menu-item index="/orders">
            <i class="el-icon-s-order"></i>
            <span>工单管理</span>
          </el-menu-item>
          <el-menu-item index="/inspection">
            <i class="el-icon-search"></i>
            <span>巡检管理</span>
          </el-menu-item>
          <el-menu-item index="/inventory">
            <i class="el-icon-suitcase"></i>
            <span>库存管理</span>
          </el-menu-item>
          <el-menu-item index="/efficiency">
            <i class="el-icon-s-data"></i>
            <span>月度报告</span>
          </el-menu-item>
          <el-menu-item index="/rights">
            <i class="el-icon-s-marketing"></i>
            <span>用户权益</span>
          </el-menu-item>

        </el-menu>

        <!-- 右侧：消息 + 主题切换 + 用户 -->
        <div class="header-right">
          <!-- 主题切换 -->
          <!-- <el-tooltip :content="theme==='light'?'切换暗色模式':'切换亮色模式'" placement="bottom">
            <span class="header-action" @click="toggleTheme">
              <i :class="theme==='light' ? 'el-icon-moon' : 'el-icon-sunny'" style="font-size:18px"></i>
            </span>
          </el-tooltip> -->

          <!-- 消息中心 -->
          <el-badge :value="unreadCount" :hidden="!unreadCount" class="header-badge">
            <span class="header-action" title="消息中心" @click="goNotifications">
              <i class="el-icon-bell" style="font-size:18px"></i>
            </span>
          </el-badge>

          <!-- 用户中心 -->
          <el-dropdown @command="handleCommand" trigger="click">
            <span class="user-center">
              <el-avatar :size="32" :src="avatarUrl" icon="el-icon-user-solid" class="user-avatar"></el-avatar>
              <span class="user-name">{{ username }}</span>
              <i class="el-icon-arrow-down" style="font-size:12px;color:var(--text-muted)"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="personalDetails">
                <i class="el-icon-user"></i> 个人信息
              </el-dropdown-item>
              <el-dropdown-item command="logout" divided>
                <i class="el-icon-switch-button"></i> 退出登录
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </el-header>

      <!-- 内容区 -->
      <el-main class="layout-content">
        <router-view />
      </el-main>
    </el-container>

    <!-- 登录页全屏 -->
    <router-view v-else />
  </div>
</template>

<script>
import request from '@/api/request'

export default {
  name: 'App',
  data() {
    return {
      theme: localStorage.getItem('maint-theme') || 'light',
      unreadCount: 0,
      notifyPollTimer: null
    }
  },
  computed: {
    activeMenu() { return this.$route.path },
    isAdmin() { return this.$store.getters.isAdmin },
    username() { return this.$store.state.user?.realName || this.$store.state.user?.username || '维保人员' },
    avatarUrl() { return this.$store.state.user?.avatar || '' }
  },
  methods: {
    toggleTheme() {
      this.theme = this.theme === 'light' ? 'dark' : 'light'
      localStorage.setItem('maint-theme', this.theme)
    },
    handleCommand(cmd) {
      if (cmd === 'logout') {
        this.$store.dispatch('logout')
        this.$router.push('/login')
      }
      if (cmd === 'personalDetails') {
        this.$router.push('/personalDetails')
      }
    },
    goNotifications() {
      this.$router.push({ path: '/rights', query: { tab: 'notifications' } }).catch(() => {})
    },
    goDashboard() {
      this.$router.push('/dashboard').catch(() => {})
    },
    async loadUnreadCount() {
      // 登录/注册页不请求，避免无 token 触发 401 提示
      if (this.$route.path === '/login' || this.$route.path === '/register') return
      try {
        const res = await request({ url: '/notifications/unread-count' })
        this.unreadCount = (res.data && res.data.count) || 0
      } catch (e) { /* 静默 */ }
    },
    // 根据当前路由启停未读消息轮询：公开页（登录/注册）不轮询
    setupNotifyPolling() {
      const isPublic = this.$route.path === '/login' || this.$route.path === '/register'
      if (isPublic) {
        if (this.notifyPollTimer) {
          clearInterval(this.notifyPollTimer)
          this.notifyPollTimer = null
        }
        return
      }
      if (!this.notifyPollTimer) {
        this.loadUnreadCount()
        this.notifyPollTimer = setInterval(() => { this.loadUnreadCount() }, 30000)
      }
    }
  },
  watch: {
    '$store.state.user': {
      handler(user) {
        if (user && user.pwdChanged === 0) {
          this.$nextTick(() => {
            this.$confirm('检测到您使用的是初始密码，为了账号安全，建议立即修改密码。', '安全提醒', {
              confirmButtonText: '去修改', cancelButtonText: '稍后再说', type: 'warning'
            }).then(() => { this.$router.push('/personalDetails') }).catch(() => {})
          })
        }
      },
      immediate: true
    },
    '$route'() {
      this.setupNotifyPolling()
    }
  },
  mounted() {
    // 等待初始路由解析后再启停轮询：mounted 早于首次路由解析，
    // 此时 $route.path 仍是默认值，会被误判为非公开页而立即发起未读消息请求触发 401
    this.$router.onReady(() => {
      this.setupNotifyPolling()
    })
  },
  beforeDestroy() {
    if (this.notifyPollTimer) clearInterval(this.notifyPollTimer)
  }
}
</script>

<style scoped>
.layout-container { height: 100%; }
.layout-header {
  display: flex; align-items: center;
  padding: 0 20px;
  position: sticky; top: 0; z-index: 100;
  transition: background var(--transition-normal), border var(--transition-normal);
}

/* logo */
.header-left { display: flex; align-items: center; margin-right: 24px; }
.header-logo { display: flex; align-items: center; gap: 10px; cursor: pointer; }
.logo-icon { flex-shrink: 0; }
.logo-text {
  font-size: 16px; font-weight: 700;
  background: var(--gradient-primary);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  background-clip: text;
  white-space: nowrap;
}

/* 菜单 */
.header-menu {
  flex: 1;
  border-bottom: none !important;
}
.header-menu >>> .el-menu-item {
  margin: 0 4px; font-size: 14px;
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
}
.header-menu >>> .el-menu-item:hover {
  background: var(--color-primary-bg) !important;
}
.header-menu >>> .el-menu-item.is-active {
  background: var(--color-primary-bg) !important;
  font-weight: 600;
}

/* 右侧区域 */
.header-right {
  margin-left: auto;
  display: flex; align-items: center; gap: 4px;
}
.header-action {
  width: 36px; height: 36px;
  display: flex; align-items: center; justify-content: center;
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}
.header-action:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}
.header-badge { margin: 0 4px; }
.header-badge >>> .el-badge__content {
  background: var(--color-error) !important;
}

/* 用户中心 */
.user-center {
  display: flex; align-items: center; gap: 8px;
  padding: 4px 12px 4px 4px;
  border-radius: var(--radius-xl);
  cursor: pointer;
  transition: all var(--transition-fast);
  margin-left: 8px;
}
.user-center:hover {
  background: var(--bg-tertiary);
}
.user-avatar >>> img {
  border-radius: 50% !important;
}
.user-name {
  font-size: 13px; color: var(--text-primary);
  max-width: 80px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

/* 内容区 */
.layout-content {
  background: var(--bg-primary);
  min-height: calc(100vh - 56px);
  padding: 0;
}
</style>
